import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import {
  Users, Building2, FileText, Shield, Gavel, CreditCard, Mail,
  Activity, Search, AlertTriangle, CheckCircle, Clock, ChevronRight,
  Settings, Eye, Edit2, X, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const VALID_ROLES = ["owner", "buyer", "lawyer", "broker", "admin"];

export default function AdminDashboard() {
  const { API } = useAuth();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [pios, setPios] = useState([]);
  const [issues, setIssues] = useState([]);
  const [emails, setEmails] = useState([]);
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editIssue, setEditIssue] = useState(null);
  const [propSearch, setPropSearch] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [API]);

  const fetchTab = useCallback(async (tab) => {
    try {
      const endpoints = {
        users: "/admin/users", properties: `/admin/properties${propSearch ? `?search=${encodeURIComponent(propSearch)}` : ""}`,
        pios: "/admin/pios", issues: "/admin/issues", emails: "/admin/emails", logs: "/admin/activity-logs",
      };
      if (!endpoints[tab]) return;
      const res = await fetch(`${API}${endpoints[tab]}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (tab === "users") setUsers(data);
        else if (tab === "properties") setProperties(data);
        else if (tab === "pios") setPios(data);
        else if (tab === "issues") setIssues(data);
        else if (tab === "emails") setEmails(data);
        else if (tab === "logs") setLogs(data);
      }
    } catch (e) { console.error(e); }
  }, [API, propSearch]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab !== "overview") fetchTab(activeTab); }, [activeTab, fetchTab]);

  const globalSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await fetch(`${API}/admin/search`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ query: searchQuery }),
      });
      if (res.ok) setSearchResults(await res.json());
    } catch (e) { console.error(e); }
  };

  const updateUser = async (userId, update) => {
    try {
      await fetch(`${API}/admin/users/${userId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(update),
      });
      setEditUser(null);
      fetchTab("users");
    } catch (e) { console.error(e); }
  };

  const updateIssue = async (issueId, update) => {
    try {
      await fetch(`${API}/admin/issues/${issueId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(update),
      });
      setEditIssue(null);
      fetchTab("issues");
    } catch (e) { console.error(e); }
  };

  const TABS = [
    { key: "overview", label: "Overview", icon: Activity },
    { key: "users", label: "Users", icon: Users },
    { key: "properties", label: "Properties", icon: Building2 },
    { key: "pios", label: "PIO Network", icon: Shield },
    { key: "issues", label: "Issues", icon: AlertTriangle },
    { key: "emails", label: "Email Log", icon: Mail },
    { key: "logs", label: "Activity Logs", icon: Activity },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Settings className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-serif text-2xl text-white">Admin Panel</h2>
          </div>
          <p className="text-white/40 text-sm font-sans">Platform oversight & management</p>
        </div>
        {/* Global Search */}
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <Input data-testid="admin-global-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && globalSearch()} placeholder="Search users, properties..."
              className="pl-10 w-60 bg-white/5 border-white/10 text-white rounded-sm text-xs" />
          </div>
          <Button onClick={globalSearch} variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] rounded-sm text-xs">Search</Button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="bg-[#11130F] border border-[#D4AF37]/20 rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#D4AF37] text-xs font-sans">Search Results for "{searchQuery}"</p>
            <button onClick={() => setSearchResults(null)} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-wider font-sans mb-1">Users ({searchResults.users?.length})</p>
              {searchResults.users?.slice(0, 5).map(u => (
                <div key={u.user_id} className="text-xs font-sans text-white/60 py-1">{u.name} — {u.email} ({u.role})</div>
              ))}
            </div>
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-wider font-sans mb-1">Properties ({searchResults.properties?.length})</p>
              {searchResults.properties?.slice(0, 5).map(p => (
                <div key={p.property_id} className="text-xs font-sans text-white/60 py-1">{p.name} — {p.address}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6">
        {TABS.map(t => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-sans rounded-sm transition-colors ${activeTab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/40 border border-white/5 hover:text-white/60"}`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        stats ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { label: "Total Users", value: stats.total_users, icon: Users, color: "#D4AF37" },
              { label: "Properties", value: stats.total_properties, icon: Building2, color: "#556B2F" },
              { label: "Documents", value: stats.total_documents, icon: FileText, color: "#D4AF37" },
              { label: "Court Cases", value: stats.total_cases, icon: Gavel, color: "#F59E0B" },
              { label: "Active Subs", value: stats.active_subscriptions, icon: Shield, color: "#556B2F" },
              { label: "Revenue", value: `₹${(stats.total_revenue || 0).toLocaleString()}`, icon: CreditCard, color: "#D4AF37" },
              { label: "Open Issues", value: stats.open_issues, icon: AlertTriangle, color: "#F59E0B" },
              { label: "Emails Sent", value: stats.total_emails, icon: Mail, color: "#71717A" },
            ].map(s => (
              <div key={s.label} className="bg-[#11130F] border border-white/10 rounded-sm p-4">
                <s.icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
                <p className="font-serif text-xl text-white">{s.value}</p>
                <p className="text-white/40 text-[10px] font-sans uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
          {/* Role Distribution */}
          <div className="bg-[#11130F] border border-white/10 rounded-sm p-5">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">User Roles Distribution</p>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.role_counts || {}).map(([role, count]) => (
                <div key={role} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />
                  <span className="text-white text-sm font-sans capitalize">{role}</span>
                  <span className="text-white/40 text-xs font-sans">({count})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        ) : (
          <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <p className="text-white/40 text-sm font-sans">Loading platform statistics...</p>
            <p className="text-white/20 text-xs font-sans mt-1">Make sure you're in the Admin role to view this data</p>
          </div>
        )
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.user_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 flex items-center justify-between hover:border-[#D4AF37]/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#D4AF37] font-serif text-sm">{u.name?.charAt(0) || "U"}</span>
                </div>
                <div>
                  <p className="text-white font-sans text-sm">{u.name} {u.disabled && <span className="text-red-400 text-[9px] ml-1">DISABLED</span>}</p>
                  <p className="text-white/40 text-[10px] font-sans">{u.email} | {u.properties_count} properties</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize bg-[#D4AF37]/10 text-[#D4AF37]">{u.role}</span>
                <button data-testid={`edit-user-${u.user_id}`} onClick={() => setEditUser(u)} className="text-white/20 hover:text-[#D4AF37] transition-colors"><Edit2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Properties Tab */}
      {activeTab === "properties" && (
        <div>
          <div className="flex gap-2 mb-4">
            <Input value={propSearch} onChange={e => setPropSearch(e.target.value)} placeholder="Search by name, survey, village..." className="bg-white/5 border-white/10 text-white rounded-sm text-xs" />
            <Button onClick={() => fetchTab("properties")} variant="outline" className="border-white/10 text-white/50 rounded-sm text-xs">Filter</Button>
          </div>
          <div className="space-y-2">
            {properties.map(p => (
              <div key={p.property_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-sans text-sm">{p.name}</p>
                  <p className="text-white/40 text-[10px] font-sans">{p.address} | Owner: {p.owner?.name || "N/A"} | Risk: {p.risk_score ?? "N/A"}</p>
                </div>
                <div className="flex items-center gap-2">
                  {p.survey_number && <span className="text-white/20 text-[10px] font-sans">Survey {p.survey_number}</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize bg-white/5 text-white/40">State {p.lifecycle_state ?? 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PIO Network Tab */}
      {activeTab === "pios" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pios.map(p => (
            <div key={p.pio_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-serif text-base text-white">{p.name}</h4>
                <span className={`text-[10px] px-2 py-0.5 rounded-sm font-sans ${p.status === "available" ? "bg-[#556B2F]/10 text-[#556B2F]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>{p.status}</span>
              </div>
              <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-sans mb-2">{p.specialization}</p>
              <div className="flex items-center gap-4 text-[10px] font-sans text-white/40">
                <span>{p.region}</span><span>{p.experience_years}y exp</span>
                <span>Active: {p.active_assignments}</span><span>Total: {p.total_assignments}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Issues Tab */}
      {activeTab === "issues" && (
        <div className="space-y-2">
          {issues.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-8 text-center">
              <AlertTriangle className="w-8 h-8 text-white/10 mx-auto mb-2" />
              <p className="text-white/30 text-xs font-sans">No issues reported</p>
            </div>
          ) : issues.map(i => (
            <div key={i.issue_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 hover:border-[#D4AF37]/30 transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-sans text-sm">{i.title}</p>
                  <p className="text-white/40 text-[10px] font-sans">{i.property?.name || i.property_id} | By: {i.user?.name || "N/A"} | Type: {i.issue_type} | Priority: {i.priority}</p>
                  {i.description && <p className="text-white/30 text-[10px] font-sans mt-1">{i.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize ${i.status === "open" ? "bg-[#F59E0B]/10 text-[#F59E0B]" : i.status === "resolved" || i.status === "closed" ? "bg-[#556B2F]/10 text-[#556B2F]" : "bg-[#D4AF37]/10 text-[#D4AF37]"}`}>{i.status}</span>
                  <button onClick={() => setEditIssue(i)} className="text-white/20 hover:text-[#D4AF37]"><Edit2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email Log Tab */}
      {activeTab === "emails" && (
        <div className="space-y-2">
          {emails.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-8 text-center">
              <Mail className="w-8 h-8 text-white/10 mx-auto mb-2" />
              <p className="text-white/30 text-xs font-sans">No emails sent yet</p>
            </div>
          ) : emails.map(e => (
            <div key={e.email_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-sans text-xs font-medium truncate">{e.subject}</p>
                  <p className="text-white/40 text-[10px] font-sans">To: {e.to_name} ({e.to_email})</p>
                  <p className="text-white/20 text-[10px] font-sans mt-1 line-clamp-2">{e.body?.substring(0, 150)}...</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <span className="text-[9px] px-2 py-0.5 rounded-sm font-sans bg-[#556B2F]/10 text-[#556B2F]">{e.status}</span>
                  <p className="text-white/20 text-[9px] font-sans mt-1">{new Date(e.sent_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity Logs Tab */}
      {activeTab === "logs" && (
        <div className="space-y-1">
          {logs.slice(0, 50).map(l => (
            <div key={l.audit_id} className="bg-[#11130F] border border-white/5 rounded-sm p-3 flex items-center gap-3">
              <div className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center shrink-0">
                <Activity className="w-3 h-3 text-white/30" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-white text-[10px] font-sans">{l.action}</span>
                <span className="text-white/20 text-[10px] font-sans ml-2">{l.category}</span>
                {l.detail && <span className="text-white/20 text-[10px] font-sans ml-2">— {l.detail?.substring(0, 60)}</span>}
              </div>
              <span className="text-white/15 text-[9px] font-sans shrink-0">{new Date(l.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl">Edit User</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <p className="text-white/60 text-sm font-sans">{editUser.name} ({editUser.email})</p>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Role</label>
                <Select defaultValue={editUser.role} onValueChange={v => updateUser(editUser.user_id, { role: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {VALID_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => updateUser(editUser.user_id, { disabled: !editUser.disabled })}
                  variant="outline" className={`flex-1 rounded-sm text-xs ${editUser.disabled ? "border-[#556B2F] text-[#556B2F]" : "border-red-400/30 text-red-400"}`}>
                  {editUser.disabled ? "Enable User" : "Disable User"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Issue Dialog */}
      <Dialog open={!!editIssue} onOpenChange={() => setEditIssue(null)}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl">Manage Issue</DialogTitle></DialogHeader>
          {editIssue && (
            <div className="space-y-4">
              <p className="text-white/60 text-sm font-sans">{editIssue.title}</p>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Status</label>
                <Select defaultValue={editIssue.status} onValueChange={v => updateIssue(editIssue.issue_id, { status: v, note: `Status changed to ${v} by admin` })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {["open", "assigned", "investigating", "action_required", "resolved", "closed"].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Priority</label>
                <Select defaultValue={editIssue.priority} onValueChange={v => updateIssue(editIssue.issue_id, { priority: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#11130F] border-white/10">
                    {["low", "medium", "high", "critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
